import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormHelperText,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import DatePicker from '../../UIElements/DatePicker';
import TimePicker from '../../UIElements/TimePicker';
import {
  examScheduleValidationSchema,
  initialFormValues,
  scheduleToFormValues,
  formValuesToSchedule,
} from './scheduleValidation';
import { getLabelsByInstituteType } from '../../../utils/constants';
import React from 'react';
import { MenuProps } from '../../../utils/constants';

const ScheduleFormDialog = ({
  open,
  onClose,
  onSubmit,
  mode, // 'add' or 'edit'
  initialData,
  examTypes,
  subjects,
  departments,
  classes,
  sections,
  onDepartmentChange,
  onClassChange,
  hasConflictWithExisting,
  Department,
  instituteType,
  passPercentage,
}) => {
  const percentage = passPercentage;
  const isEditMode = mode === 'edit';
  const title = isEditMode
    ? 'Edit Examination Schedule'
    : 'Schedule New Examination';

  const { Class, Section } = getLabelsByInstituteType(instituteType);
  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      setSubmitting(true);

      // Check for conflicts
      const scheduleData = formValuesToSchedule(values, initialData?.id);
      if (hasConflictWithExisting(scheduleData, initialData?.id)) {
        setFieldError(
          'date',
          'Schedule conflict for same class/section on this date and time.',
        );
        setFieldError('startTime', '');
        setFieldError('endTime', '');
        return;
      }

      await onSubmit(scheduleData);
      //onClose();
    } catch (error) {
      console.error('Form submission error:', error);
      setFieldError('submit', 'An error occurred while saving the schedule.');
    } finally {
      setSubmitting(false);
    }
  };

  const formInitialValues = isEditMode
    ? scheduleToFormValues(initialData)
    : initialFormValues;
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <Formik
        initialValues={formInitialValues}
        validationSchema={examScheduleValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, errors, isSubmitting, setFieldValue }) => {
          React.useEffect(() => {
            if (values.marks) {
              const calculatedPassMarks = (
                values.marks *
                ((percentage ?? 35) / 100)
              ).toFixed(1);
              const roundedNumber = Math.round(calculatedPassMarks);
              setFieldValue('passMarks', roundedNumber);
            } else {
              setFieldValue('passMarks', '');
            }
          }, [values.marks, setFieldValue]);
          return (
            <Form>
              <DialogContent>
                <h6> Select Exam Type & Subject</h6>
                <Box className='grid grid-cols-1 min-[500px]:grid-cols-2  md:grid-cols-3 gap-3  mb-4 mt-3'>
                  <Field name='examType'>
                    {({ field, meta }) => (
                      <FormControl
                        size='small'
                        fullWidth
                        required
                        error={meta.touched && !!meta.error}
                      >
                        <InputLabel shrink={true}>Exam Type</InputLabel>
                        <Select
                          MenuProps={MenuProps}
                          {...field}
                          label='Exam Type'
                          value={field.value || ''}
                          displayEmpty
                        >
                          <MenuItem value='' disabled>
                            Select Exam Type
                          </MenuItem>
                          {examTypes?.map((type) => (
                            <MenuItem key={type.id} value={type.examType}>
                              {type.examType}
                            </MenuItem>
                          ))}
                        </Select>
                        {meta.touched && meta.error && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                  <Field name='subjectName'>
                    {({ field, meta }) => (
                      <FormControl
                        size='small'
                        fullWidth
                        required
                        error={meta.touched && !!meta.error}
                      >
                        <InputLabel shrink={true}>Subject Name</InputLabel>
                        <Select
                          MenuProps={MenuProps}
                          {...field}
                          label='Subject Name'
                          value={field.value || ''}
                          displayEmpty
                        >
                          <MenuItem value='' disabled>
                            Select Subject
                          </MenuItem>
                          {subjects?.map((subject) => (
                            <MenuItem
                              key={subject.id}
                              value={subject.subjectName}
                            >
                              {subject.subjectName}
                            </MenuItem>
                          ))}
                        </Select>
                        {meta.touched && meta.error && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                  <div></div>
                </Box>
                <h6>Select Class Details</h6>
                <Box className='grid grid-cols-1 min-[500px]:grid-cols-2  md:grid-cols-3 gap-3  mb-4 mt-3'>
                  <Field name='departmentName'>
                    {({ field, meta }) => (
                      <FormControl
                        size='small'
                        fullWidth
                        required
                        error={meta.touched && !!meta.error}
                      >
                        <InputLabel shrink={true}>{Department}</InputLabel>
                        <Select
                          MenuProps={MenuProps}
                          {...field}
                          label={Department}
                          value={field.value || ''}
                          displayEmpty
                          onChange={(e) => {
                            setFieldValue('departmentName', e.target.value);
                            setFieldValue('classesName', '');
                            setFieldValue('sectionName', ''); // Reset class and section when department changes
                            onDepartmentChange(e.target.value);
                          }}
                        >
                          <MenuItem value='' disabled>
                            Select {Department}
                          </MenuItem>
                          {departments?.length === 0 && (
                            <MenuItem value=''>{`No ${Department} Available`}</MenuItem>
                          )}
                          {departments?.map((obj) => (
                            <MenuItem key={obj.id} value={obj.department}>
                              {obj.department}
                            </MenuItem>
                          ))}
                        </Select>
                        {meta.touched && meta.error && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                  <Field name='classesName'>
                    {({ field, meta }) => (
                      <FormControl
                        size='small'
                        fullWidth
                        required
                        error={meta.touched && !!meta.error}
                      >
                        <InputLabel shrink={true}>{Class}</InputLabel>
                        <Select
                          MenuProps={MenuProps}
                          {...field}
                          label={Class}
                          value={field.value || ''}
                          displayEmpty
                          onChange={(e) => {
                            setFieldValue('classesName', e.target.value);
                            setFieldValue('sectionName', ''); // Reset section when class changes
                            onClassChange(e.target.value);
                          }}
                        >
                          <MenuItem value='' disabled>
                            Select {Class}
                          </MenuItem>
                          {classes.map((cls) => (
                            <MenuItem key={cls.id} value={cls.className}>
                              {cls.className}
                            </MenuItem>
                          ))}
                        </Select>
                        {meta.touched && meta.error && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                  {instituteType.toLowerCase() === 'college' && (
                    <Field name='sectionName'>
                      {({ field, meta }) => (
                        <FormControl
                          size='small'
                          fullWidth
                          error={meta.touched && !!meta.error}
                        >
                          <InputLabel shrink={true}>{Section}</InputLabel>
                          <Select
                            MenuProps={MenuProps}
                            {...field}
                            label={Section}
                            value={field.value || ''}
                            displayEmpty
                          >
                            <MenuItem value='' disabled>
                              Select {Section}
                            </MenuItem>
                            {sections.map((obj) => (
                              <MenuItem key={obj.id} value={obj.section}>
                                {obj.section}
                              </MenuItem>
                            ))}
                          </Select>
                          {meta.touched && meta.error && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  )}
                </Box>
                <h6>Exam Timings & Marks</h6>
                <Box className='grid grid-cols-1 min-[500px]:grid-cols-2  md:grid-cols-3 gap-x-2 gap-y-3 mb-4 mt-3'>
                  {' '}
                  <Field name='date'>
                    {({ field, meta, form }) => (
                      <DatePicker
                        value={field.value}
                        name={field.name}
                        size='small'
                        label='Exam Date'
                        required
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        allowTodayAndFuture={true}
                        allowPastDates={true}
                        showClearButton={true}
                        clearButtonLabel='Clear exam date'
                        onChange={(event, value) => {
                          form.setFieldValue(field.name, value);
                        }}
                        showCalendarIcon={false}
                      />
                    )}
                  </Field>
                  <Field name='startTime'>
                    {({ field, meta, form }) => (
                      <TimePicker
                        value={field.value}
                        name={field.name}
                        size='small'
                        label='Start Time'
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        showClearButton={true}
                        clearButtonLabel='Clear start time'
                        step={300}
                        format24Hour={true}
                        onChange={(event, value) => {
                          form.setFieldValue(field.name, value);
                        }}
                        showTimeIcon={false}
                      />
                    )}
                  </Field>
                  <Field name='endTime'>
                    {({ field, meta, form }) => (
                      <TimePicker
                        value={field.value}
                        name={field.name}
                        size='small'
                        label='End Time'
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        showClearButton={true}
                        clearButtonLabel='Clear end time'
                        step={300}
                        format24Hour={true}
                        minTime={form.values.startTime || undefined}
                        onChange={(event, value) => {
                          form.setFieldValue(field.name, value);
                        }}
                        showTimeIcon={false}
                      />
                    )}
                  </Field>
                  <Field name='marks'>
                    {({ field, meta }) => (
                      <TextField
                        sx={{
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                            {
                              display: 'none',
                            },
                        }}
                        onKeyDown={(e) => {
                          if (['-', '+', 'e', 'E'].includes(e.key)) {
                            e.preventDefault(); // block minus, plus, and scientific notation
                          }
                        }}
                        size='small'
                        {...field}
                        fullWidth
                        label='Total Marks'
                        type='number'
                        required
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        inputProps={{
                          min: 1,
                          max: 1000,
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        }}
                      />
                    )}
                  </Field>
                  <Field name='passMarks'>
                    {({ field, meta }) => (
                      <TextField
                        sx={{
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                            {
                              display: 'none',
                            },
                        }}
                        onKeyDown={(e) => {
                          if (['-', '+', 'e', 'E'].includes(e.key)) {
                            e.preventDefault(); // block minus, plus, and scientific notation
                          }
                        }}
                        size='small'
                        {...field}
                        fullWidth
                        label='Pass Marks'
                        type='number'
                        required
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        inputProps={{
                          min: 1,
                          max: values.marks || 1000,
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        }}
                      />
                    )}
                  </Field>
                </Box>
                {/* Display general form errors */}
                {errors.submit && (
                  <Box className='text-red-600 text-sm mt-2'>
                    {errors.submit}
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={onClose}
                  startIcon={<CancelIcon />}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? 'Saving...'
                    : isEditMode
                      ? 'Update Schedule'
                      : 'Schedule Exam'}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default ScheduleFormDialog;
